import SearchBar from "@src/lib/ui/SearchBar";
import Fuse from "fuse.js";
import React, { useMemo, useState } from "react";
import Spacer from "@src/lib/ui/Spacer";
import { Checkbox } from "@src/lib/ui/Checkbox";
import Text from "@src/lib/ui/Text";

type Props<T extends string> = {
  allChoices: Choice<T>[];
  selectedChoices: Choice<T>[];
  onSelectedChoicesChange: (newChoices: Choice<T>[]) => void;
  loading?: boolean;
  maxMenuHeight?: React.CSSProperties["maxHeight"];
  removeClearButton?: boolean;
  searchEnabled?: boolean;
  autoFocus?: boolean;
};

const fuseConfig = {
  keys: ["label"],
  threshold: 0.1,
};

export const MultiSelectMenuContent = <T extends string>({
  allChoices,
  selectedChoices,
  onSelectedChoicesChange,
  loading,
  maxMenuHeight = 240,
  removeClearButton,
  searchEnabled = true,
  autoFocus = false,
}: Props<T>): React.ReactElement => {
  const [search, setSearch] = useState("");
  const fuse = useMemo(() => {
    return new Fuse(allChoices, fuseConfig);
  }, [allChoices]);
  const searchResults = useMemo(() => {
    let filteredGroups = allChoices;
    if (search.length > 0) {
      filteredGroups = fuse.search(search).map((result) => result.item);
    }
    return filteredGroups;
  }, [fuse, search, allChoices]);
  const selectedChoiceValues = useMemo(
    () => selectedChoices.map((c) => c.value),
    [selectedChoices],
  );
  return (
    <div
      className="flex w-[240px] flex-col"
      style={{ maxHeight: maxMenuHeight }}
    >
      {searchEnabled && (
        <div className="p-2">
          <SearchBar
            className="w-full rounded-xl text-sm bg-gray-4-alpha"
            inputClassName="rounded-xl w-full text-sm"
            placeholder={`Search...`}
            value={search}
            onChange={setSearch}
            autoFocus={autoFocus}
          />
        </div>
      )}
      <div className="h-px w-full bg-gray-4-alpha" />
      <div className="max-h-[184px] overflow-y-auto rounded-lg bg-white">
        {allChoices.length === 0 && loading && (
          <div className="ml-5 py-3.5 text-sm text-gray-11">Loading...</div>
        )}
        {allChoices.length > 0 && searchResults.length === 0 && (
          <div className="ml-5 py-3.5 text-sm text-gray-11">No results</div>
        )}
        {searchResults.map((choice) => (
          <label
            key={choice.value}
            className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-gray-4-alpha"
          >
            <Checkbox
              checked={selectedChoiceValues.includes(choice.value)}
              onCheckedChange={() => {
                const newSelectedChoices = [...selectedChoices];
                const index = selectedChoiceValues.indexOf(choice.value);
                if (index === -1) {
                  newSelectedChoices.push(choice);
                } else {
                  newSelectedChoices.splice(index, 1);
                }
                onSelectedChoicesChange(newSelectedChoices);
              }}
            />
            <Text variant="compact-description">{choice.label}</Text>
          </label>
        ))}
        <Spacer size={searchEnabled ? 16 : 4} />
      </div>
      {!removeClearButton && allChoices.length > 0 && (
        <div className="flex h-10 min-h-[40px] items-center justify-end border-t border-gray-5-alpha">
          <div
            className="mr-3 cursor-pointer text-sm text-blue-9 hover:underline"
            onClick={() => onSelectedChoicesChange([])}
          >
            Clear
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectMenuContent;

export type Choice<T extends string | boolean = string> = {
  value: T;
  label: string;
  type?: string;
};
